import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router-dom';
import {useAlertes} from '../hooks/useAlertes';
import {NotificationApplicative} from '../types';
import keycloak from '../keycloak';
import {cleDernierVu, compterNouvelles, ecrireDernierVu, lireDernierVu, plusGrandId} from '../utils/alertesVues';

interface AlertesClocheProps {
    isCollapsed?: boolean;
}

const styleNiveau = (niveau: NotificationApplicative['niveau']) => {
    switch (niveau) {
        case 'CRITIQUE':
            return {icone: 'bi-exclamation-octagon-fill', couleur: 'text-red-500', bord: 'border-l-red-500'};
        case 'ALERTE':
            return {icone: 'bi-exclamation-triangle-fill', couleur: 'text-amber-500', bord: 'border-l-amber-500'};
        default:
            return {icone: 'bi-info-circle-fill', couleur: 'text-blue-500', bord: 'border-l-blue-500'};
    }
};

const AlertesCloche: React.FC<AlertesClocheProps> = ({isCollapsed = false}) => {
    const {alertes, nonLues, marquerLu, marquerToutLu} = useAlertes();
    const [ouvert, setOuvert] = useState(false);
    // Le badge compte ce qui est NOUVEAU depuis la derniere ouverture, pas ce qui est non lu :
    // ouvrir la cloche pour regarder la liste doit le faire disparaitre. Voir utils/alertesVues.
    const cle = cleDernierVu((keycloak.tokenParsed as any)?.preferred_username);
    const [dernierVu, setDernierVu] = useState(() => lireDernierVu(cle));
    const nouvelles = compterNouvelles(alertes, dernierVu);
    const [position, setPosition] = useState<{ bottom: number; left: number } | null>(null);
    const boutonRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();

    // Le panneau est rendu via un portail (position: fixed) pour ne pas être tronqué par
    // l'overflow de la sidebar ; il faut donc fermer sur clic extérieur à la main.
    useEffect(() => {
        if (!ouvert) return;
        const fermer = (e: MouseEvent) => {
            if (!boutonRef.current?.contains(e.target as Node)) {
                setOuvert(false);
            }
        };
        document.addEventListener('mousedown', fermer);
        return () => document.removeEventListener('mousedown', fermer);
    }, [ouvert]);

    const basculer = () => {
        if (!ouvert && boutonRef.current) {
            const rect = boutonRef.current.getBoundingClientRect();
            setPosition({bottom: window.innerHeight - rect.top + 8, left: rect.left});
        }
        if (!ouvert) {
            // Tout ce qui est affiche a l ouverture devient VU. Les alertes non cliquees restent
            // en gras dans la liste : vues ne veut pas dire traitees.
            const vu = Math.max(dernierVu, plusGrandId(alertes));
            setDernierVu(vu);
            ecrireDernierVu(cle, vu);
        }
        setOuvert(o => !o);
    };

    const ouvrirAlerte = (alerte: NotificationApplicative) => {
        if (!alerte.lu) {
            marquerLu(alerte.id);
        }
        if (alerte.lien) {
            navigate(alerte.lien);
            setOuvert(false);
        }
    };

    return (
        <>
            <button
                ref={boutonRef}
                onClick={basculer}
                aria-label={nouvelles > 0 ? `Alertes (${nouvelles} nouvelles)` : 'Alertes'}
                className={`relative flex items-center rounded-xl text-white/80 hover:bg-white/10 hover:text-white transition-all duration-200 ${
                    isCollapsed ? 'justify-center p-2 w-full' : 'gap-3 px-3 py-2 w-full'
                }`}
            >
                <i className="bi bi-bell text-[17px] w-5 text-center"/>
                {!isCollapsed && <span className="text-[13px] font-medium">Alertes</span>}
                {nouvelles > 0 && (
                    <span
                        className={`absolute bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${
                            isCollapsed ? 'top-0.5 right-1.5' : 'right-3'
                        }`}
                    >
                        {nouvelles > 99 ? '99+' : nouvelles}
                    </span>
                )}
            </button>

            {ouvert && position && createPortal(
                <div
                    style={{position: 'fixed', bottom: position.bottom, left: position.left}}
                    className="w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-200 z-[1100] overflow-hidden"
                >
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-800 text-sm">Alertes</h3>
                        {nonLues > 0 && (
                            <button
                                onClick={marquerToutLu}
                                className="text-xs text-blue-600 hover:text-blue-800"
                            >
                                Tout marquer comme lu
                            </button>
                        )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                        {alertes.length === 0 ? (
                            <div className="px-4 py-8 text-center text-gray-500">
                                <i className="bi bi-bell-slash text-3xl mb-2 block"/>
                                <p className="text-sm">Aucune alerte</p>
                            </div>
                        ) : (
                            alertes.map(alerte => {
                                const style = styleNiveau(alerte.niveau);
                                return (
                                    <button
                                        key={alerte.id}
                                        onClick={() => ouvrirAlerte(alerte)}
                                        className={`w-full text-left px-4 py-3 border-b border-gray-100 border-l-4 ${style.bord} hover:bg-gray-50 transition-colors ${
                                            alerte.lu ? 'opacity-60' : ''
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <i className={`bi ${style.icone} ${style.couleur} mt-0.5`}/>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm text-gray-800 ${alerte.lu ? '' : 'font-semibold'}`}>
                                                    {alerte.titre}
                                                </p>
                                                {alerte.message && (
                                                    <p className="text-xs text-gray-600 mt-0.5">{alerte.message}</p>
                                                )}
                                                <p className="text-[11px] text-gray-400 mt-1">
                                                    {new Date(alerte.dateCreation).toLocaleString('fr-FR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default AlertesCloche;
